/**
 * @file WithRWSOverview.jsx
 * @description 원료칭량관리시스템(RWS) 소개 목업 페이지입니다.
 */

import {
  AlertTriangle,
  ArrowRight,
  Barcode,
  ClipboardList,
  Database,
  FileClock,
  Scale,
  Settings,
  ShieldCheck,
  Target,
} from 'lucide-react';
import { solRws } from '../../assets';
import { solutionItems } from '../../constants/solutionData';

const RWS_DATA = solutionItems.find((item) => item.id === 'rws');

const CAPABILITY_META = [
  { category: 'WORK ORDER', Icon: ClipboardList },
  { category: 'LOT TRACEABILITY', Icon: FileClock },
  { category: 'BARCODE & SCALE', Icon: Barcode },
  { category: 'E-SIGN & AUDIT', Icon: ShieldCheck },
];

const CAPABILITIES = RWS_DATA.keyModules.map((module, index) => ({
  num: String(index + 1).padStart(2, '0'),
  category: CAPABILITY_META[index].category,
  title: module.label,
  Icon: CAPABILITY_META[index].Icon,
  desc: module.desc ?? RWS_DATA.features[index] ?? RWS_DATA.highlights[index % RWS_DATA.highlights.length].text,
}));

const DASHBOARD_BARS = [
  { day: '월', total: 40, done: 28 },
  { day: '화', total: 45, done: 32 },
  { day: '수', total: 38, done: 30 },
  { day: '목', total: 51, done: 36 },
  { day: '금', total: 43, done: 34 },
];

const CHART_TICKS = [52, 45, 40, 35, 30, 25, 20, 15, 10, 5, 0];

const WEIGHING_ROWS = [
  { lot: 'P260418-001', process: '칭량', status: '진행중', itemName: 'Lactose Monohydrate', itemCode: 'RM-1001', qty: '12.5 kg' },
  { lot: 'P260418-002', process: '칭량', status: '대기', itemName: 'Microcrystalline Cellulose', itemCode: 'RM-1002', qty: '8.0 kg' },
  { lot: 'P260419-001', process: '칭량', status: '진행중', itemName: 'Povidone K30', itemCode: 'RM-1010', qty: '1.2 kg' },
  { lot: 'P260419-002', process: '칭량', status: '대기', itemName: 'Magnesium Stearate', itemCode: 'RM-1022', qty: '0.4 kg' },
  { lot: 'P260420-001', process: '칭량', status: '진행중', itemName: 'Sodium Starch Glycolate', itemCode: 'RM-1031', qty: '2.1 kg' },
  { lot: 'P260421-001', process: '칭량', status: '대기', itemName: 'Croscarmellose Sodium', itemCode: 'RM-1034', qty: '1.8 kg' },
  { lot: 'P260422-001', process: '칭량', status: '진행중', itemName: 'Hydroxypropyl Cellulose', itemCode: 'RM-1040', qty: '3.5 kg' },
  { lot: 'P260423-001', process: '칭량', status: '대기', itemName: 'Pregelatinized Starch', itemCode: 'RM-1050', qty: '4.0 kg' },
  { lot: 'P260424-001', process: '칭량', status: '진행중', itemName: 'Colloidal Silicon Dioxide', itemCode: 'RM-1062', qty: '0.6 kg' },
];

const PROCESS_STEPS = [
  { n: '01', title: '제조 지시', desc: '생산 오더와 배합 기준을 칭량 작업으로 전환' },
  { n: '02', title: '원료 스캔', desc: '원료·Lot·사용기한을 바코드로 검증' },
  { n: '03', title: '저울 칭량', desc: '목표 중량과 허용 오차를 실시간 비교' },
  { n: '04', title: '이중 확인', desc: '작업자·검토자 전자서명과 사유 기록' },
  { n: '05', title: '기록 보존', desc: '칭량 이력과 감사 로그를 리포트로 연결' },
];

const SUMMARY_ICONS = [Target, Settings, Database];

function RWSHero() {
  return (
    <section className="rws-hero">
      <div className="container">
        <div className="rws-hero-inner">
          <div className="rws-hero-visual">
            <img src={solRws} alt="RWS 칭량관리 시스템 화면 예시" className="rws-hero-image" />
          </div>

          <div className="rws-hero-text">
            <p className="rws-eyebrow">{RWS_DATA.badge}</p>
            <h1 className="rws-hero-title">
              {RWS_DATA.portfolioTitle[0]}
              <br />
              <em className="rws-accent">{RWS_DATA.portfolioTitle[1]}</em>
            </h1>
            <p className="rws-hero-desc">{RWS_DATA.summary}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CapabilityCard({ item }) {
  const Icon = item.Icon;

  return (
    <article className="rws-cap-card">
      <span className="rws-cap-num">{item.num}</span>
      <Icon size={28} color="#c98a2b" strokeWidth={1.8} aria-hidden="true" />
      <p className="rws-cap-category">{item.category}</p>
      <h3 className="rws-cap-title">{item.title}</h3>
      <p className="rws-cap-desc">{item.desc}</p>
    </article>
  );
}

function RWSCapabilities() {
  return (
    <section className="rws-caps">
      <div className="container">
        <div className="rws-caps-header">
          <p className="rws-eyebrow">CORE CAPABILITIES</p>
          <h2 className="rws-caps-title">
            {RWS_DATA.title}의 <em className="rws-accent">핵심 모듈</em>
          </h2>
          <p className="rws-caps-intro">{RWS_DATA.portfolioDesc}</p>
        </div>

        <div className="rws-caps-grid">
          {CAPABILITIES.map((cap) => (
            <CapabilityCard key={cap.num} item={cap} />
          ))}
        </div>
      </div>
    </section>
  );
}

function RWSDashboard() {
  const maxTotal = 52;

  return (
    <div className="rws-dashboard" aria-label="RWS 칭량 현황 대시보드">
      <div className="rws-bar-chart" aria-label="일별 전체건수와 완료건수 막대 그래프">
        <div className="rws-chart-plot">
          <div className="rws-chart-y-axis" aria-hidden="true">
            {CHART_TICKS.map((tick) => (
              <span key={tick}>{tick}</span>
            ))}
          </div>
          <div className="rws-chart-bars">
            {DASHBOARD_BARS.map((item) => (
              <div key={item.day} className="rws-chart-day">
                <div className="rws-chart-pair">
                  <span
                    className="rws-chart-bar is-total"
                    style={{ '--bar-size': `${(item.total / maxTotal) * 100}%` }}
                    aria-label={`${item.day} 전체 ${item.total}건`}
                  />
                  <span
                    className="rws-chart-bar is-done"
                    style={{ '--bar-size': `${(item.done / maxTotal) * 100}%` }}
                    aria-label={`${item.day} 완료 ${item.done}건`}
                  />
                </div>
                <strong>{item.day}</strong>
              </div>
            ))}
          </div>
        </div>
        <div className="rws-chart-legend">
          <span><i className="is-total" /> 전체건수</span>
          <span><i className="is-done" /> 완료건수</span>
        </div>
      </div>

      <div className="rws-weighing-list">
        <div className="rws-list-title-row">
          <h4>칭량작업 현황</h4>
        </div>
        <div className="rws-work-table">
          <div className="rws-table-head">
            <span>제조번호</span>
            <span>공정</span>
            <span>칭량상태</span>
            <span>품목명</span>
            <span>품목코드</span>
            <span>지시량</span>
          </div>
          {WEIGHING_ROWS.map((row) => (
            <div key={`${row.lot}-${row.itemCode}`} className="rws-table-row">
              <span>{row.lot}</span>
              <span>{row.process}</span>
              <span>{row.status}</span>
              <span>{row.itemName}</span>
              <span>{row.itemCode}</span>
              <span>{row.qty}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RWSDashboardSection() {
  return (
    <section className="rws-proposal rws-proposal-dashboard">
      <div className="container">
        <div className="rws-proposal-inner">
          <div className="rws-proposal-text">
            <p className="rws-eyebrow">WEIGHING CONTROL ROOM</p>
            <h2 className="rws-proposal-title">
              일별 칭량 흐름과 <em className="rws-accent">작업 리스트를 한 화면에서</em>
            </h2>
            <p className="rws-proposal-desc">{RWS_DATA.highlights[2].text}</p>

            <div className="rws-value-list">
              <div><Scale size={18} /> {RWS_DATA.features[3]}</div>
              <div><AlertTriangle size={18} /> {RWS_DATA.features[4]}</div>
              <div><FileClock size={18} /> {RWS_DATA.features[7]}</div>
            </div>
          </div>

          <RWSDashboard />
        </div>
      </div>
    </section>
  );
}

function RWSProcessSection() {
  return (
    <section className="rws-proposal rws-proposal-process">
      <div className="container">
        <div className="rws-process-head">
          <p className="rws-eyebrow">STANDARD WEIGHING FLOW</p>
          <h2 className="rws-proposal-title">
            지시 부터 <em className="rws-accent">칭량까지</em>
          </h2>
          <p className="rws-proposal-desc">{RWS_DATA.portfolioDesc}</p>
        </div>

        <div className="rws-process-steps">
          <div className="rws-process-line" aria-hidden="true" />
          {PROCESS_STEPS.map((step) => (
            <article key={step.n} className="rws-process-step">
              <span className="rws-process-num">{step.n}</span>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </article>
          ))}
        </div>

        <div className="rws-before-after">
          <div className="rws-compare-col">
            <p className="rws-compare-label">Before</p>
            <h3>수기 기록과 분리된 칭량 작업</h3>
            <ul>
              <li>원료 확인과 저울값 기록을 작업자 판단에 의존</li>
              <li>오차·재칭량 사유가 늦게 취합</li>
              <li>감사 대응 시 관련 기록을 별도로 추적</li>
            </ul>
          </div>
          <div className="rws-compare-arrow" aria-hidden="true">
            <ArrowRight size={24} />
          </div>
          <div className="rws-compare-col is-after">
            <p className="rws-compare-label">After</p>
            <h3>검증 중심의 전자 칭량 흐름</h3>
            <ul>
              <li>원료·Lot·중량을 단계별로 자동 검증</li>
              <li>오차와 재작업 위험을 실시간으로 확인</li>
              <li>전자서명과 감사 로그를 한 번에 보존</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function RWSFeatureEffectSection() {
  return (
    <section className="rws-final-info">
      <div className="container">
        <div className="rws-final-head">
          <p className="rws-eyebrow">WEIGHING MANAGEMENT SUMMARY</p>
          <h2 className="rws-final-title">
            칭량 업무의 <em className="rws-accent">검증 기준</em>을 한 번에 정리합니다.
          </h2>
        </div>

        <div className="sol-info-grid fade-in rws-info-grid">
          <div className="sol-summary-stack">
            {RWS_DATA.highlights.map((item, index) => {
              const Icon = SUMMARY_ICONS[index] ?? Target;
              return (
                <article key={item.label} className="sol-summary-card">
                  <div className="sol-summary-icon" aria-hidden="true">
                    <Icon size={30} strokeWidth={1.8} />
                  </div>
                  <div>
                    <h4>{item.label}</h4>
                    <p>{item.text}</p>
                  </div>
                </article>
              );
            })}
          </div>

          <article className="sol-list-card">
            <h3 className="sol-list-card-title">주요 기능</h3>
            <ul className="sol-card-list">
              {RWS_DATA.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          </article>

          <article className="sol-list-card">
            <h3 className="sol-list-card-title">도입 효과</h3>
            <ul className="sol-card-list">
              {RWS_DATA.effects.map((effect) => (
                <li key={effect}>{effect}</li>
              ))}
            </ul>
          </article>
        </div>
      </div>
    </section>
  );
}

export default function WithRWSOverview() {
  return (
    <>
      <RWSHero />
      <RWSCapabilities />
      <RWSDashboardSection />
      <RWSProcessSection />
      <RWSFeatureEffectSection />
    </>
  );
}
